<template>
  <transition name="modal-fade">
    <div v-if="visible" class="excel-upload-modal" @mousedown.self="$emit('close')">
      <div class="modal-content modal-wide">
        <h3>上传 Excel — {{ tabLabel }}</h3>

        <!-- 步骤 1：选择文件 -->
        <div v-if="wizardStep === 1" class="wizard-step">
          <p class="wizard-step-label">步骤 1 / {{ hasConflicts ? 3 : 2 }} · 选择文件</p>
          <input ref="fileInput" type="file" class="file-input-hidden" accept=".xlsx,.xls" @change="onFilePick" />
          <div
            class="excel-drop-zone"
            :class="{ 'is-dragging': dropActive, 'has-file': !!pendingFile }"
            @click="triggerFileInput"
            @dragenter.prevent="dropActive = true"
            @dragleave.prevent="dropActive = false"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
            <div v-if="!pendingFile" class="drop-zone-empty">
              <span class="drop-title">将 Excel 拖到此处，或<span class="drop-link">点击选择文件</span></span>
              <span class="drop-sub">支持 .xlsx、.xls，首行需为表头</span>
            </div>
            <div v-else class="drop-zone-file" @click.stop>
              <div class="file-meta">
                <span class="file-name">{{ pendingFile.name }}</span>
                <span class="file-size">{{ formatFileSize(pendingFile.size) }}</span>
              </div>
              <button type="button" class="btn-remove-file" @click.stop="removePendingFile">更换</button>
            </div>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="$emit('close')">取消</button>
            <button type="button" class="btn btn-primary" :disabled="!pendingFile || parseLoading" @click="runParse">
              {{ parseLoading ? '解析中…' : '下一步' }}
            </button>
          </div>
        </div>

        <!-- 步骤 2：列映射与预览 -->
        <div v-else-if="wizardStep === 2" class="wizard-step wizard-step--fill">
          <p class="wizard-step-label">步骤 2 / {{ hasConflicts ? 3 : 2 }} · 列映射与预览</p>
          <div class="wizard-step-body">
          <p class="map-intro">
            已自动匹配 Excel 表头与数据库字段。带 <span class="req">*</span> 的关键列为必填映射；
            其它列可选「全部为空 (NULL)」表示该字段整列留空。
          </p>
          <div class="map-table-wrap">
            <table class="map-table">
              <thead>
                <tr>
                  <th>数据库字段</th>
                  <th>Excel 列</th>
                  <th>匹配状态</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="f in fieldDefs"
                  :key="f.fieldKey"
                  :class="{ 'row-error': f.required && isFieldMissing(f.fieldKey), 'row-key': f.required }"
                >
                  <td>
                    {{ f.label }}
                    <span v-if="f.required" class="req">*</span>
                    <span v-if="f.required" class="tag-key">关键列</span>
                  </td>
                  <td>
                    <select v-model="mapping[f.fieldKey]" class="map-select">
                      <option :value="MAP_NONE">{{ f.required ? '— 请选择 —' : '全部为空 (NULL)' }}</option>
                      <option v-for="h in excelHeaders" :key="h" :value="h">{{ h }}</option>
                    </select>
                  </td>
                  <td>
                    <span v-if="mapping[f.fieldKey] && mapping[f.fieldKey] !== MAP_NONE" class="match-auto">已映射</span>
                    <span v-else-if="!f.required" class="match-null">NULL</span>
                    <span v-else class="match-missing">待选择</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mapping-preview-block">
            <h4 class="mapping-preview-title">映射预览（前 {{ previewRowCount }} 行）</h4>
            <p v-if="!canPreview" class="preview-placeholder">请先完成所有关键列的映射。</p>
            <div v-else-if="previewMappedRows.length" class="preview-table-wrap">
              <table class="preview-table">
                <thead>
                  <tr><th v-for="col in previewColumns" :key="col">{{ col }}</th></tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in previewMappedRows" :key="idx">
                    <td v-for="col in previewColumns" :key="col">{{ displayCell(row[col]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="preview-placeholder">当前预览行中暂无有效关键列数据。</p>
          </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="wizardStep = 1">上一步</button>
            <button type="button" class="btn btn-secondary" @click="clearMapping">清空映射</button>
            <button type="button" class="btn btn-primary" :disabled="!canSubmit || conflictCheckLoading" @click="runConflictCheck">
              {{ conflictCheckLoading ? '检测冲突中…' : '下一步' }}
            </button>
          </div>
        </div>

        <!-- 步骤 3：冲突审核 -->
        <div v-else-if="wizardStep === 3" class="wizard-step wizard-step--fill">
          <p class="wizard-step-label">步骤 3 / 3 · 冲突审核</p>

          <div class="wizard-step-body">
          <!-- 统计摘要 -->
          <div class="conflict-summary">
            <div class="summary-chip chip-new">
              <span class="chip-num">{{ conflictSummary.noConflictCount }}</span>
              <span class="chip-label">条新增</span>
            </div>
            <div class="summary-chip chip-conflict">
              <span class="chip-num">{{ conflicts.length }}</span>
              <span class="chip-label">条冲突</span>
            </div>
            <div class="summary-chip chip-skip" v-if="conflictSummary.ignoredDuplicates > 0">
              <span class="chip-num">{{ conflictSummary.ignoredDuplicates }}</span>
              <span class="chip-label">条完全重复（自动跳过）</span>
            </div>
          </div>

          <p class="conflict-intro">
            以下 <strong>{{ conflicts.length }}</strong> 条数据的关键列与库中已有记录匹配。
            勾选「<strong>保留旧版</strong>」则不替换；不勾选则用新数据覆盖旧行。
          </p>

          <!-- 全选 / 全不选 -->
          <div class="conflict-bulk-actions">
            <label class="bulk-check-label">
              <input type="checkbox" :checked="allKeepOld" @change="toggleAllKeepOld" />
              全部保留旧版
            </label>
            <span class="bulk-hint">（默认不勾选 = 全部用新数据替换）</span>
          </div>

          <div class="conflict-list">
            <div
              v-for="(conflict, idx) in conflicts"
              :key="conflict.conflictKey"
              class="conflict-card"
              :class="{ 'is-keep-old': conflictResolutions[conflict.conflictKey] === 'old' }"
            >
              <!-- 卡片头 -->
              <div class="conflict-card-header">
                <label class="keep-old-label">
                  <input
                    type="checkbox"
                    :checked="conflictResolutions[conflict.conflictKey] === 'old'"
                    @change="toggleResolution(conflict.conflictKey, $event.target.checked)"
                  />
                  <span>保留旧版</span>
                </label>
                <span class="conflict-index">冲突 {{ idx + 1 }} / {{ conflicts.length }}</span>
                <span class="key-values">
                  {{ Object.entries(conflict.keyValues).map(([k, v]) => `${k}: ${v}`).join('  ·  ') }}
                </span>
              </div>

              <!-- diff 表格 -->
              <div v-if="conflict.diff.length > 0" class="diff-table-wrap">
                <table class="diff-table">
                  <thead>
                    <tr>
                      <th class="diff-col-field">字段</th>
                      <th class="diff-col-old">旧值（库中现有）</th>
                      <th class="diff-col-arrow"></th>
                      <th class="diff-col-new">新值（Excel 上传）</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in conflict.diff" :key="d.col">
                      <td class="diff-field">{{ d.col }}</td>
                      <td class="diff-old">{{ d.old || '（空）' }}</td>
                      <td class="diff-arrow">→</td>
                      <td class="diff-new">{{ d.new || '（空）' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="diff-same">关键列匹配，其余字段值完全相同</p>
            </div>
          </div>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="wizardStep = 2">上一步</button>
            <button type="button" class="btn btn-primary" :disabled="importLoading" @click="submitImport">
              {{ importLoading ? '导入中…' : `确认导入（${keepNewCount} 条替换，${keepOldCount} 条保留旧版）` }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import * as adminService from '@/services/adminApiService.js';

const MAP_NONE = '__none__';
const PREVIEW_ROW_COUNT = 5;

export default {
  name: 'ExcelUploadWizard',
  props: {
    visible: { type: Boolean, default: false },
    tableName: { type: String, required: true },
    tabLabel: { type: String, default: '数据' },
    isEditMode: { type: Boolean, default: false },
    initialFile: { type: File, default: null }
  },
  emits: ['close', 'success', 'error'],
  data() {
    return {
      MAP_NONE,
      wizardStep: 1,
      pendingFile: null,
      dropActive: false,
      parseLoading: false,
      importLoading: false,
      conflictCheckLoading: false,
      fieldDefs: [],
      excelHeaders: [],
      previewRows: [],
      suggestedMapping: {},
      mapping: {},
      // Step 3
      conflicts: [],
      conflictResolutions: {},  // { [conflictKey]: 'old' | 'new' }
      conflictSummary: { noConflictCount: 0, ignoredDuplicates: 0, totalRows: 0, filteredEmptyRows: 0 }
    };
  },
  computed: {
    previewRowCount() { return PREVIEW_ROW_COUNT; },
    requiredFields() { return (this.fieldDefs || []).filter((f) => f.required); },
    canSubmit() {
      return this.requiredFields.every((f) => {
        const m = this.mapping[f.fieldKey];
        return m && m !== MAP_NONE;
      });
    },
    canPreview() { return this.canSubmit; },
    previewColumns() {
      const cols = [];
      (this.fieldDefs || []).forEach((f) => {
        const m = this.mapping[f.fieldKey];
        if (m && m !== MAP_NONE) cols.push(f.fieldKey);
      });
      return cols.slice(0, 8);
    },
    previewMappedRows() {
      if (!this.canPreview) return [];
      const out = [];
      for (const row of this.previewRows || []) {
        const mapped = this.mapRow(row);
        if (mapped) out.push(mapped);
        if (out.length >= PREVIEW_ROW_COUNT) break;
      }
      return out;
    },
    lsKey() { return `excel_column_map_${this.tableName}_v1`; },
    hasConflicts() { return this.conflicts.length > 0; },
    keepOldCount() {
      return this.conflicts.filter(c => this.conflictResolutions[c.conflictKey] === 'old').length;
    },
    keepNewCount() { return this.conflicts.length - this.keepOldCount; },
    allKeepOld() {
      return this.conflicts.length > 0 && this.conflicts.every(c => this.conflictResolutions[c.conflictKey] === 'old');
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetWizard();
        if (this.initialFile) this.pendingFile = this.initialFile;
      }
    },
    initialFile(file) {
      if (file && this.visible) this.pendingFile = file;
    }
  },
  methods: {
    resetWizard() {
      this.wizardStep = 1;
      this.pendingFile = this.initialFile || null;
      this.fieldDefs = [];
      this.excelHeaders = [];
      this.previewRows = [];
      this.suggestedMapping = {};
      this.mapping = {};
      this.parseLoading = false;
      this.importLoading = false;
      this.conflictCheckLoading = false;
      this.conflicts = [];
      this.conflictResolutions = {};
      this.conflictSummary = { noConflictCount: 0, ignoredDuplicates: 0, totalRows: 0, filteredEmptyRows: 0 };
    },
    triggerFileInput() { this.$refs.fileInput?.click(); },
    onFilePick(e) {
      const file = e.target.files?.[0];
      if (file) this.assignFile(file);
    },
    onDrop(e) {
      this.dropActive = false;
      const file = e.dataTransfer?.files?.[0];
      if (file) this.assignFile(file);
    },
    assignFile(file) {
      const name = file.name || '';
      if (!/\.(xlsx|xls)$/i.test(name)) {
        this.$emit('error', '请选择 .xlsx 或 .xls 文件');
        return;
      }
      this.pendingFile = file;
    },
    removePendingFile() {
      this.pendingFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    formatFileSize(bytes) {
      if (bytes == null || Number.isNaN(bytes)) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    mergeCacheAndSuggested(suggested) {
      let cache = {};
      try {
        const raw = localStorage.getItem(this.lsKey);
        if (raw) cache = JSON.parse(raw);
      } catch (err) { cache = {}; }
      const out = {};
      (this.fieldDefs || []).forEach((f) => {
        const k = f.fieldKey;
        if (suggested[k]) out[k] = suggested[k];
        else if (cache[k] && cache[k] !== MAP_NONE) out[k] = cache[k];
        else out[k] = MAP_NONE;
      });
      return out;
    },
    async runParse() {
      if (!this.pendingFile) return;
      this.parseLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        const data = await adminService.parseExcelUpload(this.tableName, fd);
        this.fieldDefs = data.fields || [];
        this.excelHeaders = data.headers || [];
        this.previewRows = data.previewRows || [];
        this.suggestedMapping = data.suggestedMapping || {};
        this.mapping = this.mergeCacheAndSuggested(this.suggestedMapping);
        this.wizardStep = 2;
      } catch (e) {
        this.$emit('error', e.message || '解析失败');
      } finally {
        this.parseLoading = false;
      }
    },
    isFieldMissing(fieldKey) {
      const m = this.mapping[fieldKey];
      return !m || m === MAP_NONE;
    },
    clearMapping() {
      (this.fieldDefs || []).forEach((f) => {
        this.$set(this.mapping, f.fieldKey, MAP_NONE);
      });
    },
    mapRow(row) {
      const m = this.mapping;
      for (const f of this.requiredFields) {
        const h = m[f.fieldKey];
        if (!h || h === MAP_NONE) return null;
        const v = row[h];
        if (v === '' || v === undefined || v === null) return null;
      }
      const out = {};
      (this.fieldDefs || []).forEach((f) => {
        const h = m[f.fieldKey];
        if (!h || h === MAP_NONE) { out[f.fieldKey] = null; return; }
        const v = row[h];
        out[f.fieldKey] = v === '' || v === undefined ? null : v;
      });
      return out;
    },
    displayCell(v) {
      if (v === undefined || v === null || v === '') return '—';
      return typeof v === 'number' ? v : String(v);
    },
    saveCache() {
      try {
        localStorage.setItem(this.lsKey, JSON.stringify({ ...this.mapping }));
      } catch (err) { /* ignore */ }
    },

    // ── Step 2 → 3: conflict check ──────────────────────────────
    async runConflictCheck() {
      if (!this.canSubmit || !this.pendingFile) return;
      this.conflictCheckLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        fd.append('mapping', JSON.stringify(this.mapping));
        console.log('[ExcelUploadWizard] 开始冲突检测, tableName=', this.tableName);
        const result = await adminService.checkUploadConflicts(this.tableName, fd);
        console.log('[ExcelUploadWizard] 冲突检测结果:', result);

        this.conflicts = result.conflicts || [];
        this.conflictResolutions = {};
        this.conflictSummary = {
          noConflictCount: result.noConflictCount || 0,
          ignoredDuplicates: result.ignoredDuplicates || 0,
          totalRows: result.totalRows || 0,
          filteredEmptyRows: result.filteredEmptyRows || 0
        };

        if (this.conflicts.length > 0) {
          this.wizardStep = 3;
        } else if (result.ignoredDuplicates > 0 && result.noConflictCount === 0) {
          // 所有行都与库中数据完全相同，无需导入
          this.$emit('success', {
            skipped: true,
            message: `所有 ${result.ignoredDuplicates} 条数据与库中已有记录完全相同，无需操作。`,
            stats: { ignoredFullDuplicate: result.ignoredDuplicates, insertedUnique: 0, replacedWithNewUpload: 0, keptOldCount: 0 }
          });
          this.$emit('close');
        } else {
          // 无冲突，直接导入
          await this.submitImport();
        }
      } catch (e) {
        console.error('[ExcelUploadWizard] 冲突检测失败:', e);
        this.$emit('error', e.message || '冲突检测失败');
      } finally {
        this.conflictCheckLoading = false;
      }
    },

    // ── Step 3: resolution helpers ──────────────────────────────
    toggleResolution(conflictKey, keepOld) {
      this.$set(this.conflictResolutions, conflictKey, keepOld ? 'old' : 'new');
    },
    toggleAllKeepOld(e) {
      const val = e.target.checked ? 'old' : 'new';
      this.conflicts.forEach(c => {
        this.$set(this.conflictResolutions, c.conflictKey, val);
      });
    },

    // ── Final submit ────────────────────────────────────────────
    async submitImport() {
      if (!this.pendingFile) return;
      this.importLoading = true;
      try {
        const fd = new FormData();
        fd.append('file', this.pendingFile);
        fd.append('mapping', JSON.stringify(this.mapping));
        fd.append('resolutions', JSON.stringify(this.conflictResolutions));

        const response = await adminService.uploadExcelMappedData(this.tableName, fd);
        this.saveCache();
        this.$emit('success', response);
        this.$emit('close');
      } catch (e) {
        this.$emit('error', e.message || '导入失败');
      } finally {
        this.importLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.excel-upload-modal {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  z-index: 2000;
  padding: 24px 16px;
  box-sizing: border-box;
  overflow: hidden;
}
.modal-content {
  background: #fff; border-radius: 10px; padding: 24px;
  width: min(960px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  justify-self: center;
  align-self: center;
}
.modal-content > h3 {
  flex-shrink: 0;
  margin: 0 0 16px;
}
.modal-wide { width: min(960px, 96vw); }
.wizard-step--fill {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.wizard-step-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}
.wizard-step-label { font-size: 13px; color: #666; margin: 0 0 8px; flex-shrink: 0; }
.map-intro { font-size: 13px; color: #555; margin: 0 0 16px; line-height: 1.5; }
.file-input-hidden { display: none; }

.excel-drop-zone {
  border: 2px dashed #c5cde0; border-radius: 8px; padding: 32px 20px;
  text-align: center; cursor: pointer; background: #fafbfd;
  transition: border-color 0.2s, background 0.2s;
}
.excel-drop-zone.is-dragging, .excel-drop-zone:hover { border-color: #4a6cf7; background: #f0f4ff; }
.drop-title { display: block; font-size: 15px; color: #333; }
.drop-link { color: #4a6cf7; text-decoration: underline; }
.drop-sub { display: block; font-size: 12px; color: #888; margin-top: 6px; }
.drop-zone-file { display: flex; align-items: center; justify-content: center; gap: 16px; }
.file-name { font-weight: 500; }
.file-size { font-size: 12px; color: #888; margin-left: 8px; }
.btn-remove-file { border: 1px solid #ddd; background: #fff; padding: 4px 12px; border-radius: 4px; cursor: pointer; }

.map-table-wrap { overflow: auto; border: 1px solid #e8e8e8; border-radius: 6px; margin-bottom: 16px; }
.map-table, .preview-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.map-table th, .map-table td, .preview-table th, .preview-table td { border: 1px solid #eee; padding: 8px 10px; text-align: left; }
.map-table th { background: #f5f7fb; position: sticky; top: 0; }
.row-error { background: #fff5f5; }
.row-key td:first-child { font-weight: 500; }
.req { color: #e53935; margin-left: 2px; }
.tag-key { display: inline-block; font-size: 11px; background: #e8f0fe; color: #3367d6; padding: 1px 6px; border-radius: 3px; margin-left: 6px; }
.map-select { width: 100%; min-width: 140px; padding: 4px 6px; border: 1px solid #ddd; border-radius: 4px; }
.match-auto { color: #2e7d32; font-size: 12px; }
.match-null { color: #888; font-size: 12px; }
.match-missing { color: #e53935; font-size: 12px; }
.mapping-preview-block { margin-top: 8px; }
.mapping-preview-title { font-size: 14px; margin: 0 0 8px; color: #333; }
.preview-table-wrap { overflow: auto; border: 1px solid #e8e8e8; border-radius: 6px; }
.preview-placeholder { font-size: 13px; color: #888; }

/* ── Step 3: conflict review ──────────────────────────────────── */
.conflict-summary {
  display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 14px;
}
.summary-chip {
  display: flex; align-items: baseline; gap: 6px;
  padding: 6px 14px; border-radius: 20px; font-size: 13px;
}
.chip-num { font-size: 20px; font-weight: 700; line-height: 1; }
.chip-label { font-size: 12px; }
.chip-new { background: #e8f5e9; color: #2e7d32; }
.chip-conflict { background: #fff3e0; color: #e65100; }
.chip-skip { background: #f3f3f3; color: #666; }

.conflict-intro { font-size: 13px; color: #444; margin: 0 0 12px; line-height: 1.6; }

.conflict-bulk-actions {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 14px; padding: 8px 12px;
  background: #f8f9ff; border-radius: 6px; border: 1px solid #e0e4f0;
}
.bulk-check-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; cursor: pointer; }
.bulk-hint { font-size: 12px; color: #888; }

.conflict-list { display: flex; flex-direction: column; gap: 12px; height: auto; }

.conflict-card {
  border: 1px solid #e8e8e8; border-radius: 8px; overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.conflict-card.is-keep-old {
  border-color: #b0bec5;
  background: #f9f9f9;
  opacity: 0.7;
}

.conflict-card-header {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px; background: #fafafa; border-bottom: 1px solid #eee;
}
.keep-old-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: #2e3968; cursor: pointer;
  white-space: nowrap;
}
.keep-old-label input[type="checkbox"] { width: 15px; height: 15px; cursor: pointer; accent-color: #2e3968; }
.conflict-index { font-size: 11px; color: #999; white-space: nowrap; }
.key-values { font-size: 12px; color: #555; font-family: monospace; }

.diff-table-wrap { overflow-x: auto; }
.diff-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.diff-table th, .diff-table td { padding: 7px 12px; border-bottom: 1px solid #f0f0f0; text-align: left; }
.diff-table th { font-size: 11px; color: #888; font-weight: 600; background: #fafafa; }
.diff-col-field { width: 22%; }
.diff-col-old { width: 36%; }
.diff-col-arrow { width: 5%; text-align: center; color: #bbb; }
.diff-col-new { width: 36%; }
.diff-field { color: #444; font-weight: 500; }
.diff-old { color: #c62828; background: #fff5f5; }
.diff-new { color: #2e7d32; background: #f1f8f1; }
.diff-arrow { text-align: center; color: #bbb; }
.diff-same { padding: 10px 14px; font-size: 12px; color: #888; font-style: italic; margin: 0; }

.modal-actions {
  display: flex; gap: 8px; justify-content: flex-end;
  flex-shrink: 0;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid #eee;
}
.btn { padding: 8px 16px; border-radius: 6px; border: none; cursor: pointer; font-size: 14px; }
.btn-primary { background: #4a6cf7; color: #fff; }
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary { background: #f0f0f0; color: #333; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
</style>
